import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

export type TimerActiveForm = 'start' | 'manual-add' | 'edit';

export type TimerFormDraft = {
  projectId: string | null;
  taskId: string | null;
  tagIds: string[];
  description: string;
  billableOverride: boolean | null;
  date: string | null;
  startTime: string | null;
  endTime: string | null;
  // Manual-add duration readout (MNT-08): the "how long" field, kept in sync with
  // endTime − startTime; typing into it recomputes endTime = startTime + duration.
  duration: string | null;
};

export type TimerPageState = {
  activeForm: TimerActiveForm;
  editingEntryId: string | null;
  formDraft: TimerFormDraft;
  submitInProgress: boolean;
  lastErrorKey: string | null;
  pendingDeleteEntryId: string | null;
};

const emptyDraft: TimerFormDraft = {
  projectId: null,
  taskId: null,
  tagIds: [],
  description: '',
  billableOverride: null,
  date: null,
  startTime: null,
  endTime: null,
  duration: null,
};

const initialState: TimerPageState = {
  activeForm: 'start',
  editingEntryId: null,
  formDraft: emptyDraft,
  submitInProgress: false,
  lastErrorKey: null,
  pendingDeleteEntryId: null,
};

export const timerPageSlice = createSlice({
  name: 'timerPage',
  initialState,
  reducers: {
    setActiveForm(state, action: PayloadAction<TimerActiveForm>) {
      state.activeForm = action.payload;
    },
    setFormProjectId(state, action: PayloadAction<string | null>) {
      state.formDraft.projectId = action.payload;
      // Reset dependent fields when the project changes.
      state.formDraft.taskId = null;
      state.formDraft.tagIds = [];
      state.formDraft.billableOverride = null;
    },
    setFormTaskId(state, action: PayloadAction<string | null>) {
      state.formDraft.taskId = action.payload;
    },
    setFormTagIds(state, action: PayloadAction<string[]>) {
      state.formDraft.tagIds = action.payload;
    },
    setFormDescription(state, action: PayloadAction<string>) {
      state.formDraft.description = action.payload;
    },
    setFormBillableOverride(state, action: PayloadAction<boolean | null>) {
      state.formDraft.billableOverride = action.payload;
    },
    setFormDate(state, action: PayloadAction<string | null>) {
      state.formDraft.date = action.payload;
    },
    setFormStartTime(state, action: PayloadAction<string | null>) {
      state.formDraft.startTime = action.payload;
    },
    setFormEndTime(state, action: PayloadAction<string | null>) {
      state.formDraft.endTime = action.payload;
    },
    setFormDuration(state, action: PayloadAction<string | null>) {
      state.formDraft.duration = action.payload;
    },
    setPendingDeleteEntryId(state, action: PayloadAction<string | null>) {
      state.pendingDeleteEntryId = action.payload;
    },
    setEditingEntryId(state, action: PayloadAction<string | null>) {
      state.editingEntryId = action.payload;
    },
    setSubmitInProgress(state, action: PayloadAction<boolean>) {
      state.submitInProgress = action.payload;
    },
    setLastErrorKey(state, action: PayloadAction<string | null>) {
      state.lastErrorKey = action.payload;
    },
    prefillEditForm(
      state,
      action: PayloadAction<{
        entryId: string;
        projectId: string;
        taskId: string | null;
        tagIds: string[];
        description: string;
        billable: boolean;
        date: string;
        startTime: string;
        endTime: string | null;
      }>,
    ) {
      const { entryId, projectId, taskId, tagIds, description, billable, date, startTime, endTime } =
        action.payload;
      state.editingEntryId = entryId;
      state.activeForm = 'edit';
      state.formDraft.projectId = projectId;
      state.formDraft.taskId = taskId;
      state.formDraft.tagIds = tagIds;
      state.formDraft.description = description;
      state.formDraft.billableOverride = billable;
      state.formDraft.date = date;
      state.formDraft.startTime = startTime;
      state.formDraft.endTime = endTime;
      state.formDraft.duration = null;
      state.lastErrorKey = null;
    },
    prefillActivityFromSuggestion(
      state,
      action: PayloadAction<{
        projectId: string;
        taskId: string | null;
        tagIds: string[];
        description: string;
        billable: boolean;
      }>,
    ) {
      const { projectId, taskId, tagIds, description, billable } = action.payload;
      // Suggestion-select prefill (MNT-18 / UC-95) — works in both start and manual-add
      // modes. Copies the activity's classification metadata only; the time fields
      // (date/start/end/duration) and the active form mode are left untouched: on the
      // Start form there are none (INV-021 — server-stamped), and on the manual-add
      // form the User's already-entered times are preserved.
      state.formDraft.projectId = projectId;
      state.formDraft.taskId = taskId;
      state.formDraft.tagIds = tagIds;
      state.formDraft.description = description;
      state.formDraft.billableOverride = billable;
      state.lastErrorKey = null;
    },
    prefillManualAddForm(
      state,
      action: PayloadAction<{
        projectId: string;
        taskId: string | null;
        tagIds: string[];
        description: string;
        billable: boolean;
        date: string;
        startTime: string;
        endTime: string | null;
      }>,
    ) {
      const { projectId, taskId, tagIds, description, billable, date, startTime, endTime } =
        action.payload;
      state.editingEntryId = null;
      state.activeForm = 'manual-add';
      state.formDraft.projectId = projectId;
      state.formDraft.taskId = taskId;
      state.formDraft.tagIds = tagIds;
      state.formDraft.description = description;
      state.formDraft.billableOverride = billable;
      state.formDraft.date = date;
      state.formDraft.startTime = startTime;
      state.formDraft.endTime = endTime;
      state.lastErrorKey = null;
    },
    resetForm(state) {
      state.formDraft = emptyDraft;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    resetFormAfterManualSubmit(state) {
      // The date outlives the submit (MNT-21 / PREF-4) so consecutive same-day entries
      // need no re-typing; the mode is left alone so the form stays on manual-add.
      state.formDraft = { ...emptyDraft, date: state.formDraft.date };
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    resetPage() {
      return initialState;
    },
  },
});

export const {
  setActiveForm,
  setFormProjectId,
  setFormTaskId,
  setFormTagIds,
  setFormDescription,
  setFormBillableOverride,
  setFormDate,
  setFormStartTime,
  setFormEndTime,
  setFormDuration,
  setPendingDeleteEntryId,
  setEditingEntryId,
  setSubmitInProgress,
  setLastErrorKey,
  prefillEditForm,
  prefillActivityFromSuggestion,
  prefillManualAddForm,
  resetForm,
  resetFormAfterManualSubmit,
  resetPage,
} = timerPageSlice.actions;

type AppRootState = { timerPage: TimerPageState };

export const selectTimerPage = (state: AppRootState) => state.timerPage;
